/**
 * El rubro de una empresa cliente.
 *
 * No es una etiqueta: decide **con qué se compara**. Una empresa RETAIL compara
 * contra tiendas de ecommerce; una COMIDA compara contra restaurantes en
 * aplicaciones de delivery, donde el precio que se ve en la app NO es el que cobra
 * el local — hay una comisión en el medio. Son dos análisis distintos.
 */
export type TipoDeEmpresa = 'RETAIL' | 'COMIDA';

/** Cómo se dice y se pinta cada rubro. Un solo lugar para las tres pantallas. */
export const RUBROS: Record<TipoDeEmpresa, { etiqueta: string; clases: string }> = {
  RETAIL: { etiqueta: 'Retail', clases: 'border-sky-500/40 text-sky-700 dark:text-sky-400' },
  COMIDA: {
    etiqueta: 'Comida',
    clases: 'border-orange-500/40 text-orange-700 dark:text-orange-400',
  },
};

import { PlanTier, SubscriptionStatus, UserRole } from '@/types';

/** Una empresa con sus métricas, para la comparación lado a lado. */
export interface EmpresaComparada {
  id: string;
  nombre: string;
  slug: string;
  rubro: TipoDeEmpresa;
  activa: boolean;
  creada: string;
  plan: { nombre: string; tier: string; vence: string; prueba: boolean } | null;
  usuarios: number;
  usuariosActivos: number;
  /** `null` significa que nadie de esa empresa entró nunca. */
  ultimoIngreso: string | null;
  cotizaciones: number;
  cotizaciones30d: number;
  ordenes: number;
  productos: number;
}

export interface TotalesDeRubro {
  rubro: TipoDeEmpresa;
  empresas: number;
  usuarios: number;
  cotizaciones: number;
  cotizaciones30d: number;
  ordenes: number;
  productos: number;
}

export interface ComparacionDeEmpresas {
  empresas: EmpresaComparada[];
  porRubro: TotalesDeRubro[];
  /** Cuántas se dejaron afuera por no tener actividad. Se informa, no se esconde. */
  ocultas: number;
  totalEmpresas: number;
}

export interface Plan {
  id: string;
  name: string;
  tier: PlanTier;
  description?: string;
  monthlyPrice: number;
  annualPrice?: number;
  maxUsers: number;
  maxQuotesPerMonth: number;
  maxPricingModels: number;
  maxOrganizations: number;
  maxMaterials: number;
  features: Record<string, boolean> | null;
  /** Módulos que incluye el plan (llaves del catálogo). Vacía = sin techo; en «Prueba Gratuita» el alta la exige no vacía. */
  modules?: string[];
  isActive: boolean;
  sortOrder: number;
  trialDays: number;
  subscriberCount?: number;
  createdAt: string;
  updatedAt: string;
}

export interface TenantSubscription {
  id: string;
  tenantId: string;
  planId: string;
  status: SubscriptionStatus;
  startDate: string;
  endDate: string;
  isTrial: boolean;
  notes?: string;
  cancelledAt?: string;
  cancelReason?: string;
  createdBy?: string;
  tenant?: { id: string; name: string; slug?: string; isActive?: boolean };
  plan?: { id: string; name: string; tier: PlanTier };
  createdAt: string;
  updatedAt: string;
}

/**
 * Reglas de la demo (343, tanda 4): lo que el servidor dice de una empresa en prueba en la lista del panel.
 * La pantalla no deduce nada: muestra estos valores (el estado sale de `estadoDeLaDemo`).
 */
export interface DemoDeLaEmpresa {
  eje: { id: string; nombre: string } | null;
  /** `SIN_TIENDAS_LEIDAS`: su rubro ya no tiene ninguna tienda leída. */
  ejeEstado: 'ELEGIDO' | 'SIN_TIENDAS_LEIDAS';
  tienda: { nombre: string } | null;
  archivadas: number;
  productos: { usados: number; tope: number | null; enElTope: boolean };
  /** Empresa archivada: el servidor ya apagó para ella «lista para la venta», «sin ingresos» y «rubro sin tiendas». */
  archivada: boolean;
  listaParaLaVenta: boolean;
  abandonada: boolean;
  diasSinIngreso: number;
  /** Demo que vence (2-nov): el plazo de la prueba, tal cual lo calcula el servidor. */
  vencimiento?: VencimientoDeLaPrueba;
}

/** El plazo de la prueba (15 días, como la Prueba Gratuita; la plataforma lo puede extender). */
export interface VencimientoDeLaPrueba {
  estado: 'VIGENTE' | 'POR_VENCER' | 'VENCIDA' | 'SIN_FECHA';
  venceEl: string | null;
  /** Días que faltan (0 = hoy); negativo = días desde que venció. */
  diasRestantes: number | null;
  esPrueba: boolean;
}

/** Los filtros de demos de la lista de empresas (`?demo=`). */
export type FiltroDeDemo =
  | 'lista-para-la-venta'
  | 'abandonada'
  | 'rubro-sin-tiendas'
  | 'por-vencer'
  | 'vencida';

export interface AdminTenant {
  id: string;
  name: string;
  slug: string;
  /** El rubro. Decide contra qué se compara esta empresa. */
  type: TipoDeEmpresa;
  email?: string;
  country?: string;
  companySize?: string;
  industrySegment?: string;
  isActive: boolean;
  /** Fecha de archivado: una archivada está fuera de uso (no es lo mismo que inactiva). */
  archivedAt?: string | null;
  /** Demo: no tiene plazo que mostrar. */
  esDemo?: boolean;
  /** Misiones de la demo hechas, o `null` si no es demo. */
  misiones?: { hechas: number; total: number } | null;
  /** El estado de la demo, o `null` si la empresa no es demo. */
  demo?: DemoDeLaEmpresa | null;
  /** Mini Prixia: tope de productos propios mientras es demo. Vacío = 100. */
  topeCatalogoDemo?: number | null;
  /** Mini Prixia: tope de personas mientras es demo (cuenta las invitaciones pendientes). Vacío = 5. */
  topePersonasDemo?: number | null;
  createdAt: string;
  userCount: number;
  subscription: {
    id: string;
    planName: string;
    planTier: PlanTier;
    status: SubscriptionStatus;
    endDate: string;
    isTrial: boolean;
  } | null;
}

export interface AdminUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  isActive: boolean;
  expiresAt?: string | null;
  lastLoginAt?: string | null;
  createdAt: string;
  tenantId: string;
  tenant: { id: string; name: string; slug?: string };
  /** L5 (1.3): cuándo se eliminó, y el correo que tenía (el de ahora es `eliminado+…`). */
  deletedAt?: string | null;
  originalEmail?: string | null;
}

export interface AdminStats {
  totalTenants: number;
  activeTenants: number;
  inactiveTenants: number;
  totalUsers: number;
  activeUsers: number;
  activeSubscriptions: number;
  expiredSubscriptions: number;
  expiringSubscriptions: number;
  totalMonthlyRevenue: number;
  planDistribution: {
    planId: string;
    planName: string;
    planTier: PlanTier;
    count: number;
    monthlyRevenue: number;
  }[];
}

export interface Pagination {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

/*
  ── Borrado de una empresa ──

  El endpoint DELETE /api/admin/tenants/:id en seco (`dryRun`) devuelve qué
  tiene la empresa y qué bloquearía el borrado. El diálogo de confirmación lo
  muestra tal cual antes de pedir el slug.
*/
export interface RevisionDeBorrado {
  empresa: { id: string; name: string; slug: string };
  resumen: {
    usuarios: number;
    usuariosActivos: number;
    invitaciones: number;
    cotizaciones: number;
    productos: number;
    clientes: number;
    modelosDePricing: number;
    materiales: number;
    ventas: number;
    ordenesDeImportacion: number;
    cotizacionesDatec: number;
    cotizacionesSolares: number;
    tiendasCompetidoras: number;
    contratos: number;
    /** C11: lecturas del catálogo de la competencia. */
    lecturasDeCatalogo: number;
    /** C11: vínculos producto propio ↔ artículo de la competencia (también archivados). */
    vinculosConLaCompetencia: number;
    /** C11: precios de la competencia cargados por planilla (importación manual). */
    preciosDeLaCompetenciaCargados: number;
  };
  /** Si hay algo acá, no se puede borrar hasta resolverlo. */
  bloqueos: string[];
  /** Quién se va con la empresa y quién conserva su cuenta porque está también en otra. */
  personas?: {
    seBorran: Array<{ nombre: string; email: string }>;
    siguenEnOtraEmpresa: Array<{ nombre: string; email: string; empresa: string }>;
  };
  /** Sólo en una demo: los correos de las cuentas que se borran quedan libres. */
  liberaCorreos?: boolean;
}

/*
  ── Reiniciar una demo ──

  POST /api/admin/tenants/:id/reiniciar-demo. Decisión 2 (3-nov): no borra nada. En seco
  (`dryRun`) devuelve qué archivaría; confirmado, cuántos productos archivó y si los
  ejemplos quedaron todos.
*/
export interface ResumenDelReinicio {
  /** Los propios activos de la tienda actual: los que se archivan. */
  productosPropios: number;
  /** Los ejemplos de la tienda actual (activos o no). */
  productosDeEjemplo: number;
  /** Los ejemplos de la tienda actual que la persona archivó: vuelven. */
  ejemplosArchivados: number;
  misionesHechas: number;
  misionesEmpezadas: number;
  /** Lo de las tiendas guardadas, que no se toca. */
  conserva: {
    tiendasGuardadas: number;
    vinculos: number;
    productos: number;
    productosPropios: number;
  };
}

export interface RevisionDeReinicio {
  empresa: { id: string; name: string };
  resumen: ResumenDelReinicio;
}

export interface ResultadoDelReinicio {
  /** Productos propios que se archivaron. */
  archivados: number;
  /** Ejemplos de la tienda actual que estaban archivados y volvieron. */
  ejemplosRestaurados: number;
  /** Ejemplos que se sembraron porque faltaban. */
  sembrados: number;
  /** `false`: el reinicio se hizo pero los ejemplos no se cargaron todos. */
  completa: boolean;
}

/*
  ── Módulos de una empresa (el "techo": lo que compró) ──

  Dos capas distintas, y conviene no confundirlas al leer este código:

  · El TECHO lo decide DATEC. Es qué módulos puede tener la empresa.
  · Los PERMISOS los decide el cliente. Es qué hace cada rol dentro de eso.

  Un módulo fuera del techo no se ve, ni en el menú ni por URL, y la API lo
  rechaza. No aparece con candado — el candado es del cliente, para sus roles.
*/

/** Un módulo elegible, con su etiqueta legible. */
export interface ModuloDelCatalogo {
  key: string;
  label: string;
  /**
   * Otros módulos que, si están incluidos, arrastran a este.
   *
   * 🔴 Importa mostrarlo: sin esto la pantalla miente. El caso real con el que se
   * descubrió es que Datos Maestros arrastra el Tarifario, así que desmarcar el
   * Tarifario mientras Datos Maestros sigue marcado **no hace nada** — y quien
   * configuró se queda creyendo que se lo quitó.
   */
  impliedBy?: { key: string; label: string }[];
}

/** Los módulos agrupados como se venden. Viene del servidor. */
export interface GrupoDeModulos {
  titulo: string;
  detalle: string | null;
  items: ModuloDelCatalogo[];
}

export interface ModulosDeEmpresa {
  // `_count.users`: cuántas personas quedan afectadas al quitar un módulo.
  tenant: { id: string; name: string; _count?: { users: number } };
  subscriptionId: string | null;
  plan: { id: string; name: string; tier: string; modules: string[] } | null;
  endDate: string | null;
  /**
   * El techo vigente. **Lista vacía = sin techo**, o sea que hoy ve todo lo que
   * sus roles le permitan. La pantalla tiene que decirlo con esas palabras:
   * "ningún módulo marcado" se lee como lo contrario de lo que significa.
   */
  modules: string[];
  /** De dónde sale el techo: del plan, o de un ajuste para esta empresa. */
  origin: 'plan' | 'tenant';
  planModules: string[];
  catalog: GrupoDeModulos[];
}

/**
 * Los permisos por rol de una empresa cliente, vistos desde la plataforma.
 *
 * Es la mitad "del cliente" de las dos capas: qué hace cada persona con lo que
 * la empresa tiene. El techo —qué compró la empresa— es `ModulosDeEmpresa`.
 */
export interface PermisosDeEmpresa {
  tenant: { id: string; name: string };
  roles: Array<{
    role: string;
    permissions: string[];
    menuItems: string[];
    /** Si la empresa ya tocó este rol; si no, hereda los valores por omisión. */
    isCustomized: boolean;
  }>;
  /*
    Listas de llaves + tablas de etiquetas, y no una lista de objetos.

    ⚠ Es la forma que el endpoint devuelve desde siempre y que consume también la
    pantalla del propio cliente. Cambiarla acá "porque queda más cómodo" ya dejó
    esa pantalla en blanco una vez.
  */
  allPermissions: string[];
  allMenuItems: string[];
  permissionLabels: Record<string, string>;
  menuItemLabels: Record<string, string>;
}

/** Una persona vista desde la plataforma, con lo que se le hizo. */
/** A3: la empresa real en la que entra modo soporte. */
export interface DestinoDeSoporte {
  tenantId: string;
  nombre: string;
  esOrigen: boolean;
  origen: string | null;
}

/** L5 (plan 335 · 1.2): el acceso de una persona a UNA empresa. */
export interface MembresiaDePersona {
  role: string;
  isActive: boolean;
  /** Por qué está apagado, si lo decidió la plataforma. `null` = lo apagó su empresa, o el archivo. */
  apagadaPor?: 'QUITADO' | 'MUDANZA' | null;
  createdAt: string;
  /** Su empresa de origen: la que figura como «su empresa». */
  esOrigen: boolean;
  customRole: { id: string; name: string } | null;
  tenant: { id: string; name: string; slug: string; isActive: boolean };
}

export interface DetalleDeUsuario {
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    role: string;
    isActive: boolean;
    expiresAt: string | null;
    lastLoginAt: string | null;
    lastActiveAt: string | null;
    createdAt: string;
    /** L5 (1.3): cuándo se eliminó y el correo que tenía. */
    deletedAt?: string | null;
    originalEmail?: string | null;
    customRole: { id: string; name: string } | null;
    tenant: {
      id: string;
      name: string;
      slug: string;
      isActive: boolean;
      _count?: { users: number };
    } | null;
  };
  /**
   * Si este admin de plataforma parece estar dentro de la empresa de un cliente.
   * `null` cuando no hay nada que avisar.
   */
  avisoDeEmpresaAjena: string | null;
  /** L5: las empresas a las que entra (o entraba), con su rol y cargo EN cada una. */
  membresias?: MembresiaDePersona[];
  historial: Array<{
    id: string;
    action: string;
    createdAt: string;
    /** El administrador real. */
    autor: { id: string; firstName: string; lastName: string; email: string } | null;
    /** A quién estaba impersonando, si la acción salió de una sesión de soporte. */
    impersonando: { id: string; firstName: string; lastName: string; email: string } | null;
  }>;
}

/**
 * Lo que devuelve el clon de una empresa.
 *
 * ⚖️ `tenantId` viene vacío en un ensayo: no se creó ninguna empresa. La pantalla
 * usa eso para saber que todavía no pasó nada.
 */
export interface ResumenDelClon {
  tenantId: string;
  slug: string;
  /** Por tabla, cuántas filas tenía el origen. Detalle técnico, va plegado. */
  filasPorTabla: Record<string, number>;
  totalDeFilas: number;
  membresiasCreadas: number;
  /** Por columna de auto-relación, cuántas se re-enlazaron. */
  reenlazados: Record<string, number>;
}

/** Lo que llena el diálogo «Cambiar rubro y tienda» (`GET /api/admin/tenants/:id/demo`). */
export interface OpcionesDeLaDemo {
  demo: DemoDeLaEmpresa;
  misiones: { hechas: number; total: number };
  actual: { sitio: string; nombre: string } | null;
  archivadas: Array<{ sitio: string; nombre: string; archivadaEl: string }>;
  ejes: Array<{ id: string; nombre: string }>;
  tiendas: Array<{
    sitio: string;
    nombre: string;
    ejeId: string | null;
    ejemplos: number;
    sugerida: boolean;
  }>;
}

/** Lo que devuelve `POST /api/admin/tenants/:id/demo/cambiar-eje`. */
/** Lo que dice el servidor al convertir una demo (`conversion` en la raíz de la respuesta). */
export interface ResultadoDeLaConversion {
  /** Las estrategias automáticas que pasaron a supervisadas (nada mueve precios sin una persona). */
  estrategiasPasadasASupervisadas: Array<{ id: string; name: string; modoAnterior: string }>;
  /** El plan con el que siguió (A, 2-nov). */
  plan?: { id: string; nombre: string } | null;
}

export interface ResultadoDelCambioDeEje {
  eje: { id: string; nombre: string };
  tienda: { sitio: string; nombre: string };
  ejemplos: { archivados: number; sembrados: number; desarchivados: number };
  sinCambios: boolean;
  /** El cambio quedó hecho pero los ejemplos de la tienda nueva no se pudieron cargar todavía. */
  siembraPendiente: boolean;
}

/** Cómo está una invitación de la demo: si todavía deja darse de alta o por qué no. */
export type EstadoDeInvitacionDemo = 'ACTIVA' | 'PAUSADA' | 'VENCIDA' | 'AGOTADA';

export interface InvitacionDemo {
  id: string;
  codigo: string;
  nombre: string;
  /** `null` = sin tope. */
  topeDeAltas: number | null;
  altasUsadas: number;
  venceEn: string | null;
  pausadaEn: string | null;
  estado: EstadoDeInvitacionDemo;
  createdAt: string;
}

export interface AltaDeInvitacionDemo {
  id: string;
  empresa: string;
  persona: string;
  correo: string;
  createdAt: string;
  tenantId: string | null;
  /** `false` si la empresa se eliminó después del alta. */
  empresaExiste: boolean;
}
